import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  value: string;
  /** Extra classes for the surrounding chip — `components__code` for our own
   *  short codes, nothing for a manufacturer's part number. */
  className?: string;
  /** What this code is, for the tooltip and for screen readers. */
  label?: string;
}

/**
 * Copy a code to the clipboard by clicking it.
 *
 * Codes exist to be carried elsewhere — into a chat, an order, a search box. A
 * short code sitting next to other short text is exactly what a mouse selection
 * misses, and one wrong character in a part number is a wrong part ordered.
 *
 * Shared rather than written per screen because there are six kinds of code
 * (`C`, `L`, `S`, `P`, `J`, `K`) plus manufacturers' part numbers, and they
 * should all behave the same way.
 */
export function CopyableCode({ value, className, label }: Props) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const timer = useRef(0);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const copy = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        // Android WebView and any non-secure context: the modern API is simply
        // absent there, and a code that cannot be copied is worse than an
        // obsolete call.
        const scratch = document.createElement('textarea');
        scratch.value = value;
        scratch.setAttribute('readonly', '');
        scratch.style.position = 'fixed';
        scratch.style.opacity = '0';
        document.body.appendChild(scratch);
        scratch.select();
        document.execCommand('copy');
        scratch.remove();
      }
      setCopied(true);
      window.clearTimeout(timer.current);
      // Long enough to read, short enough not to sit there arguing with the
      // next thing the user does.
      timer.current = window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // Refused by the browser. Saying nothing would look like it worked.
      setCopied(false);
      window.alert(value);
    }
  };

  return (
    <button
      type="button"
      className={`copyable${className ? ` ${className}` : ''}${copied ? ' is-copied' : ''}`}
      title={copied ? t('common.copied') : t('common.copyHint', { what: label ?? value })}
      onClick={() => void copy()}
    >
      {value}
      <span className="copyable__mark" aria-hidden="true">
        {copied ? '✓' : '⧉'}
      </span>
      {/* Announced rather than only shown: the tick is a colour-and-shape cue,
          and this is the only confirmation a screen reader would get. */}
      <span className="visually-hidden" role="status">
        {copied ? t('common.copied') : ''}
      </span>
    </button>
  );
}
